/**
 * Compact Editor-like markup for the Agent model: pending Word redlines stay
 * visible as <del>/<ins> instead of being flattened to accept-all plain text.
 *
 * Word-free (no Office.js / DOMParser) so vitest/node can cover the OOXML walk.
 */

import { isOurSideTrackChangesAuthor } from './quickResponses';

export type TrackChangeSide = 'our' | 'their';

export type ResolveTrackChangeAuthorSide = (author: string) => TrackChangeSide | null;

export function stripTrackChangeMarkup(text: string): string {
  return (text || '').replace(/<\/?(?:del|ins)\b[^>]*>/gi, '');
}

/**
 * Accept-all from Editor/Agent model markup: drop <del> content, unwrap <ins>.
 */
export function markupToAcceptedPlainText(text: string): string {
  let s = text || '';
  if (!/<\/?(?:del|ins)\b/i.test(s)) return s;
  while (true) {
    const open = /<del\b[^>]*>/i.exec(s);
    if (!open) break;
    const closeIdx = findMatchingClose(s, 'del', open.index + open[0].length);
    if (closeIdx === -1) {
      s = s.slice(0, open.index) + s.slice(open.index + open[0].length);
      continue;
    }
    s = s.slice(0, open.index) + s.slice(closeIdx + '</del>'.length);
  }
  return stripTrackChangeMarkup(s).replace(/\s+/g, ' ').trim();
}

export function paragraphHasTrackChangeMarkup(xml: string): boolean {
  return /<(?:[A-Za-z0-9._-]+:)?(?:ins|del|moveFrom|moveTo)\b/i.test(xml || '');
}

function escapeHtmlText(raw: string): string {
  return (raw || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function attrsFromOpenTag(attrStr: string): { author: string; side: TrackChangeSide | null } {
  const author = attrStr.match(/\bauthor="([^"]*)"/i)?.[1] ?? '';
  const sideRaw = (attrStr.match(/\bside="([^"]*)"/i)?.[1] ?? '').toLowerCase();
  const side: TrackChangeSide | null = sideRaw === 'our' || sideRaw === 'their' ? sideRaw : null;
  return { author, side };
}

function findMatchingClose(text: string, kind: 'ins' | 'del', from: number): number {
  const openRe = new RegExp(`<${kind}\\b[^>]*>`, 'ig');
  const closeRe = new RegExp(`</${kind}>`, 'ig');
  let depth = 1;
  let pos = from;
  while (pos < text.length) {
    openRe.lastIndex = pos;
    closeRe.lastIndex = pos;
    const open = openRe.exec(text);
    const close = closeRe.exec(text);
    if (!close) return -1;
    if (open && open.index < close.index) {
      depth += 1;
      pos = open.index + open[0].length;
      continue;
    }
    depth -= 1;
    if (depth === 0) return close.index;
    pos = close.index + close[0].length;
  }
  return -1;
}

/**
 * Turn Agent/Review model markup (`<ins author="…" side="our">`) into MCC preview HTML.
 * Sync — no Word round-trip — so Review findings can render redlines on first paint.
 */
export function modelMarkupToPreviewHtml(
  text: string,
  resolve?: ResolveTrackChangeAuthorSide,
): string | null {
  if (!text || !/<\/?(?:ins|del)\b/i.test(text)) return null;
  const html = convertModelMarkupToHtml(text, resolve).trim();
  return html || null;
}

function convertModelMarkupToHtml(
  text: string,
  resolve?: ResolveTrackChangeAuthorSide,
): string {
  let out = '';
  let i = 0;
  while (i < text.length) {
    if (text[i] !== '<') {
      const next = text.indexOf('<', i);
      const chunk = next === -1 ? text.slice(i) : text.slice(i, next);
      out += escapeHtmlText(chunk);
      i = next === -1 ? text.length : next;
      continue;
    }

    const rest = text.slice(i);
    const open = rest.match(/^<(ins|del)\b([^>]*)>/i);
    if (!open) {
      out += escapeHtmlText('<');
      i += 1;
      continue;
    }

    const kind = open[1].toLowerCase() as 'ins' | 'del';
    const { author, side: sideAttr } = attrsFromOpenTag(open[2]);
    const innerStart = i + open[0].length;
    const closeAt = findMatchingClose(text, kind, innerStart);
    if (closeAt === -1) {
      out += escapeHtmlText(open[0]);
      i += open[0].length;
      continue;
    }

    const innerHtml = convertModelMarkupToHtml(text.slice(innerStart, closeAt), resolve);
    const side = sideAttr ?? resolveSide(author, resolve);
    const isOur = side === 'our';
    const baseClass = kind === 'ins' ? 'track-change-insertion' : 'track-change-deletion';
    const title = author ? ` title="Author: ${escapeHtmlText(author)}"` : '';
    out += `<${kind} class="${baseClass}${isOur ? ' track-change-new' : ''}"${title}>${innerHtml}</${kind}>`;
    i = closeAt + `</${kind}>`.length;
  }
  return out;
}

function decodeXmlText(raw: string): string {
  return (raw || '')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&');
}

function escapeAttr(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

function localNameFromOpenTag(tag: string): string {
  const m = tag.match(/^<\/?([A-Za-z0-9._-]+:)?([A-Za-z0-9._-]+)/);
  return (m?.[2] || '').toLowerCase();
}

function authorFromOpenTag(openTag: string): string {
  const m =
    openTag.match(/\bw:author="([^"]*)"/i) ||
    openTag.match(/\b(?:[A-Za-z0-9._-]+:)?author="([^"]*)"/i);
  return m ? decodeXmlText(m[1]) : '';
}

function resolveSide(
  author: string,
  resolve?: ResolveTrackChangeAuthorSide,
): TrackChangeSide | null {
  const mapped = resolve?.(author) ?? null;
  if (mapped) return mapped;
  if (author && isOurSideTrackChangesAuthor(author)) return 'our';
  return null;
}

function wrapChange(
  kind: 'del' | 'ins',
  author: string,
  side: TrackChangeSide | null,
  inner: string,
): string {
  if (!inner) return '';
  const authorAttr = author ? ` author="${escapeAttr(author)}"` : '';
  const sideAttr = side ? ` side="${side}"` : '';
  return `<${kind}${authorAttr}${sideAttr}>${inner}</${kind}>`;
}

function isSelfClosing(tag: string): boolean {
  return /\/\s*>$/.test(tag);
}

/**
 * Slice the element that starts at `openStart` (index of `<`).
 * Supports `w:del` and other namespace prefixes.
 */
function sliceElement(
  xml: string,
  openStart: number,
): { openTag: string; name: string; inner: string; end: number } | null {
  const gt = xml.indexOf('>', openStart);
  if (gt < 0) return null;
  const openTag = xml.slice(openStart, gt + 1);
  const name = localNameFromOpenTag(openTag);
  if (!name) return null;
  if (isSelfClosing(openTag) || openTag.startsWith('</')) {
    return { openTag, name, inner: '', end: gt + 1 };
  }

  const openRe = new RegExp(`<(?:[A-Za-z0-9._-]+:)?${name}\\b`, 'gi');
  const closeRe = new RegExp(`</(?:[A-Za-z0-9._-]+:)?${name}\\s*>`, 'gi');
  let depth = 1;
  let pos = gt + 1;
  while (depth > 0 && pos < xml.length) {
    openRe.lastIndex = pos;
    closeRe.lastIndex = pos;
    const nextOpen = openRe.exec(xml);
    const nextClose = closeRe.exec(xml);
    if (!nextClose) break;
    const openIdx = nextOpen?.index ?? Number.POSITIVE_INFINITY;
    const closeIdx = nextClose.index;
    if (closeIdx <= openIdx) {
      depth -= 1;
      if (depth === 0) {
        return {
          openTag,
          name,
          inner: xml.slice(gt + 1, closeIdx),
          end: closeIdx + nextClose[0].length,
        };
      }
      pos = closeIdx + nextClose[0].length;
    } else {
      depth += 1;
      const nestedGt = xml.indexOf('>', openIdx);
      pos = nestedGt >= 0 ? nestedGt + 1 : openIdx + (nextOpen?.[0].length ?? 1);
    }
  }
  return { openTag, name, inner: xml.slice(gt + 1), end: xml.length };
}

function extractPlainText(xml: string): string {
  if (!xml) return '';
  let out = '';
  let i = 0;
  while (i < xml.length) {
    const lt = xml.indexOf('<', i);
    if (lt === -1) break;
    const gt = xml.indexOf('>', lt);
    if (gt === -1) break;
    const tag = xml.slice(lt, gt + 1);
    if (tag.startsWith('<?') || tag.startsWith('<!')) {
      i = gt + 1;
      continue;
    }
    const name = localNameFromOpenTag(tag);
    if (name === 'instrtext') {
      const sliced = sliceElement(xml, lt);
      i = sliced?.end ?? gt + 1;
      continue;
    }
    if (name === 't' || name === 'deltext') {
      const sliced = sliceElement(xml, lt);
      if (!sliced) {
        i = gt + 1;
        continue;
      }
      out += decodeXmlText(sliced.inner);
      i = sliced.end;
      continue;
    }
    if (name === 'tab') {
      out += '\t';
      i = gt + 1;
      continue;
    }
    if (name === 'br' || name === 'cr') {
      out += '\n';
      i = gt + 1;
      continue;
    }
    i = gt + 1;
  }
  return out;
}

function rPrKind(rInner: string): { kind: 'del' | 'ins' | null; author: string } {
  const rprMatch = rInner.match(
    /<(?:[A-Za-z0-9._-]+:)?rPr\b[\s\S]*?<\/(?:[A-Za-z0-9._-]+:)?rPr\s*>/i,
  );
  const rpr = rprMatch?.[0] || '';
  if (!rpr) return { kind: null, author: '' };
  const delOpen = rpr.match(/<(?:[A-Za-z0-9._-]+:)?del\b[^>]*>/i);
  const insOpen = rpr.match(/<(?:[A-Za-z0-9._-]+:)?ins\b[^>]*>/i);
  // A run that was inserted and then deleted has both marks; Word shows strikethrough.
  if (delOpen) return { kind: 'del', author: authorFromOpenTag(delOpen[0]) };
  if (insOpen) return { kind: 'ins', author: authorFromOpenTag(insOpen[0]) };
  return { kind: null, author: '' };
}

/**
 * Walk the contents of a w:ins / w:del so nested opposite revisions stay distinct.
 * Flattening inner text as the outer kind turns Word deletions into insertions.
 */
function formatRevisionGroup(
  kind: 'ins' | 'del',
  inner: string,
  author: string,
  side: TrackChangeSide | null,
  resolve?: ResolveTrackChangeAuthorSide,
): string {
  let out = '';
  let pending = '';
  const flush = () => {
    if (!pending) return;
    out += wrapChange(kind, author, side, pending);
    pending = '';
  };

  let i = 0;
  while (i < inner.length) {
    const lt = inner.indexOf('<', i);
    if (lt === -1) break;
    const sliced = sliceElement(inner, lt);
    if (!sliced) break;
    const { openTag, name, inner: childInner, end } = sliced;

    if (name === 'ppr' || name === 'rpr' || name === 'instrtext') {
      i = end;
      continue;
    }

    if (name === 'del' || name === 'movefrom') {
      flush();
      const nestedAuthor = authorFromOpenTag(openTag);
      out += formatRevisionGroup(
        'del',
        childInner,
        nestedAuthor,
        resolveSide(nestedAuthor, resolve),
        resolve,
      );
      i = end;
      continue;
    }
    if (name === 'ins' || name === 'moveto') {
      flush();
      const nestedAuthor = authorFromOpenTag(openTag);
      out += formatRevisionGroup(
        'ins',
        childInner,
        nestedAuthor,
        resolveSide(nestedAuthor, resolve),
        resolve,
      );
      i = end;
      continue;
    }

    if (name === 'r') {
      const runKind = rPrKind(childInner);
      const runText = extractPlainText(childInner);
      if (runText) {
        if (runKind.kind && runKind.kind !== kind) {
          flush();
          out += wrapChange(
            runKind.kind,
            runKind.author,
            resolveSide(runKind.author, resolve),
            runText,
          );
        } else {
          pending += runText;
        }
      }
      i = end;
      continue;
    }

    if (name === 't' || name === 'deltext') {
      pending += decodeXmlText(childInner);
      i = end;
      continue;
    }
    if (name === 'tab') {
      pending += '\t';
      i = end;
      continue;
    }
    if (name === 'br' || name === 'cr') {
      pending += '\n';
      i = end;
      continue;
    }

    if (!isSelfClosing(openTag) && childInner) {
      flush();
      out += formatRevisionGroup(kind, childInner, author, side, resolve);
    }
    i = end;
  }
  flush();
  return out;
}

function formatFragment(xml: string, resolve?: ResolveTrackChangeAuthorSide): string {
  if (!xml) return '';
  let out = '';
  let i = 0;
  while (i < xml.length) {
    const lt = xml.indexOf('<', i);
    if (lt === -1) break;
    const sliced = sliceElement(xml, lt);
    if (!sliced) break;
    const { openTag, name, inner, end } = sliced;

    if (name === 'instrtext') {
      i = end;
      continue;
    }

    if (name === 'del' || name === 'movefrom') {
      const author = authorFromOpenTag(openTag);
      out += formatRevisionGroup('del', inner, author, resolveSide(author, resolve), resolve);
      i = end;
      continue;
    }
    if (name === 'ins' || name === 'moveto') {
      const author = authorFromOpenTag(openTag);
      out += formatRevisionGroup('ins', inner, author, resolveSide(author, resolve), resolve);
      i = end;
      continue;
    }

    if (name === 'r') {
      const runKind = rPrKind(inner);
      const runText = extractPlainText(inner);
      if (runKind.kind && runText) {
        out += wrapChange(
          runKind.kind,
          runKind.author,
          resolveSide(runKind.author, resolve),
          runText,
        );
      } else {
        out += runText;
      }
      i = end;
      continue;
    }

    if (name === 't' || name === 'deltext') {
      out += decodeXmlText(inner);
      i = end;
      continue;
    }
    if (name === 'tab') {
      out += '\t';
      i = end;
      continue;
    }
    if (name === 'br' || name === 'cr') {
      out += '\n';
      i = end;
      continue;
    }

    // Other wrappers (w:p, w:hyperlink, …): keep walking children.
    if (!isSelfClosing(openTag) && inner) {
      out += formatFragment(inner, resolve);
    }
    i = end;
  }
  return out;
}

/** Convert a <w:p> OOXML fragment into Editor-like model text. */
export function formatOoxmlParagraphForModel(
  paragraphXml: string,
  resolveAuthorSide?: ResolveTrackChangeAuthorSide,
): string {
  if (!paragraphXml) return '';
  return formatFragment(paragraphXml, resolveAuthorSide).replace(/\s+/g, ' ').trim();
}
